import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  addDoc,
  arrayUnion,
  collection,
  collectionData,
  doc,
  DocumentData,
  Firestore,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  Query,
  QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
  writeBatch,
} from '@angular/fire/firestore';
import { BehaviorSubject, first, lastValueFrom, map, Observable, shareReplay, tap } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { RealtimeFeedService } from './realtimeFeed.service';

import { VoiceRecorder } from 'capacitor-voice-recorder';
import { v4 as uuidv4 } from 'uuid';
import { Role } from '../shared/role';

export interface Message {
  id?: string;
  message: string;
  user: string;
  timestamp: any;
}
export interface AttachmentConfig {
  allowedExtensions: string[];
  maxFileSizeMb: number;
  maxFilesPerMessage: number;
  // Hours after sending an attachment message it can still be deleted; null means no time limit.
  attachmentDeleteWindowHours: number | null;
}

export interface AttachmentItem {
  // Empty for simulated attachments, which are shown from a local object URL instead of the API.
  attachmentId?: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  previewUrl?: string;
}

// A file picked in the input box and shown in the preview tray before the message is sent.
export interface PendingAttachment {
  file: File;
  previewUrl: string | null;
}

// Attachments requested together in one API call; must not exceed the API limit (MAX_FILES_PER_MESSAGE).
const ATTACHMENT_BATCH_SIZE = 10;

const DEFAULT_ATTACHMENT_CONFIG: AttachmentConfig = {
  allowedExtensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png'],
  maxFileSizeMb: 10,
  maxFilesPerMessage: 3,
  attachmentDeleteWindowHours: 24,
};

export interface SendAttachmentOptions {
  fromUserId: string;
  userName: string;
  // Optional text sent together with the files
  text?: string;
  // 1-1 chat: recipient user id and the Firestore chat id
  toUserId?: string;
  chatId?: string;
  // group chat
  groupId?: string;
  // Only meaningful when groupId is set; defaults to 'AdminOnly' server-side when omitted.
  groupVisibility?: 'AdminOnly' | 'ShareAll';
}

// Thrown by resolveAttachmentUrl/loadAttachmentBatch so callers can special-case a specific reason
// (e.g. show a watermark for 'admin_only') instead of just a generic error message.
export class AttachmentAccessError extends Error {
  constructor(public code: string | undefined, message: string) {
    super(message);
  }
}

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private httpClient: HttpClient;
  // How many older messages one "load more" page fetches.
  public totalRecords = 50;
  // Size of the realtime window (most recent N messages); slides forward as new ones arrive instead
  // of freezing on the oldest ones once a chat passes this count.
  public pageSize = 20;
  private lastVisible: QueryDocumentSnapshot<DocumentData> | null = null;
  public hasMoreMessages = true;
  public isLoading$ = new BehaviorSubject<boolean>(false);
  public mediaRecorder!: MediaRecorder;
  audioChunks: Blob[] = [];
  audioUrl: string = '';
  recordedAudioBlob!: Blob;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private handler: HttpBackend,
    private firestore: Firestore,
    private realtimeFeedService: RealtimeFeedService
  ) {
    this.httpClient = new HttpClient(handler);
  }

  private lastKnownUserId: string | undefined;

  // ChatService is a singleton that outlives login/logout (no full page reload happens on
  // navController.navigateRoot(['/login'])), so its in-memory attachment caches must be checked
  // against the currently logged-in user on every use - otherwise a blocked viewer could see a
  // blob URL/config left over from a previous, more-privileged user in the same browser tab.
  // Checked synchronously on demand rather than via subscribing to currentUserSubject at
  // construction time: that BehaviorSubject is only created after an async storage read inside
  // AuthService's own constructor, so it may not exist yet when ChatService is constructed.
  private ensureCacheIsForCurrentUser() {
    const userId = this.authService.currentUserValue?.user_id;
    if (userId === this.lastKnownUserId) return;
    this.lastKnownUserId = userId;
    this.resolvedAttachmentUrls.clear();
    this.attachmentUrlCache.clear();
    this.knownAttachmentVisibility.clear();
    this.attachmentConfig$ = undefined;
    this.resolvedAttachmentConfig = null;
  }

  getChatId(user1: string, user2: string): string {
    // Use a consistent way to generate a chat ID based on user names
    return [user1, user2].sort().join('-').toLowerCase();
  }

  // Fire-and-forget: called once by the sender's client right after a Firestore
  // write succeeds, so the admin dashboard's Realtime Feed picks it up via the
  // backend/SignalR pipeline without every admin viewer double-logging it. The
  // backend itself decides whether to persist/broadcast based on the sender's
  // role (staff only) — errors here are swallowed since this is a secondary
  // notification path, not the chat itself.
  private logRealtimeFeed(
    isGroup: boolean,
    groupId: string | undefined,
    text: string,
    messageType: string,
    isPtt: boolean,
    recipientId?: string
  ) {
    this.realtimeFeedService
      .logChatMessage({ isGroup, groupId, recipientId, text, messageType, isPtt })
      .subscribe({ error: () => {} });
  }

  getMessages(user1: string, user2: string) {
    const chatId = this.getChatId(user1, user2);
    const messagesRef = collection(this.firestore, `chats/${chatId}/messages`);
    // desc+limit keeps this window pinned to the latest messages as new ones arrive, instead of
    // asc+limit which freezes on the OLDEST ones once a chat passes the limit; reversed back to
    // ascending below since every caller renders/merges assuming oldest-first order.
    const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(this.pageSize));

    return collectionData(q, { idField: 'id' }).pipe(
      map((messages) =>
        messages
          .map((m) => ({
            ...m,
            isPlaying: false,
            currentTime: 0,
            duration: 0,
            audioObj: null,
            // Convert Firestore timestamp to Date if needed
            timestamp: m['timestamp']?.toDate(),
          }))
          .reverse()
      )
    );
  }

  getPtt(user1: string, user2: string) {
    const chatId = this.getChatId(user1, user2);
    const messagesRef = collection(this.firestore, `chats/${chatId}/ptt`);
    const q = query(messagesRef, orderBy('timestamp', 'asc'), limit(100));

    return collectionData(q, { idField: 'id' }).pipe(
      map((messages) =>
        messages.map((m) => ({
          ...m,
          isPlaying: false,
          currentTime: 0,
          duration: 0,
          audioObj: null,
          // Convert Firestore timestamp to Date if needed
          timestamp: m['timestamp']?.toDate(),
        }))
      )
    );
  }

  getGroupMessages(groupId: string) {
    const messagesRef = collection(this.firestore, `group/${groupId}/messages`);
    // desc+limit keeps this window pinned to the latest messages as new ones arrive, instead of
    // asc+limit which freezes on the OLDEST ones once a chat passes the limit; reversed back to
    // ascending below since every caller renders/merges assuming oldest-first order.
    const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(this.pageSize));

    return collectionData(q, { idField: 'id' }).pipe(
      map((messages) =>
        messages
          .map((m) => {
            const message = {
              ...m,
              isPlaying: false,
              currentTime: 0,
              duration: 0,
              audioObj: null,
              // Lets the visibility-toggle UI recognize a group message; a 1-1 message has no such flag.
              isGroup: true,
              // Convert Firestore timestamp to Date if needed
              timestamp: m['timestamp']?.toDate(),
            };
            // Done here, at the data layer, so it applies regardless of whether Angular happens to
            // reuse or recreate the attachment component for this message (see the comment on
            // invalidateAttachmentsIfVisibilityChanged for why that can't be relied on).
            this.invalidateAttachmentsIfVisibilityChanged(message);
            return message;
          })
          .reverse()
      )
    );
  }

  getGroupPtt(groupId: string) {
    const messagesRef = collection(this.firestore, `group/${groupId}/ptt`);
    const q = query(messagesRef, orderBy('timestamp', 'asc'), limit(100));

    return collectionData(q, { idField: 'id' }).pipe(
      map((messages) =>
        messages.map((m) => ({
          ...m,
          isPlaying: false,
          currentTime: 0,
          duration: 0,
          audioObj: null,
          // Convert Firestore timestamp to Date if needed
          timestamp: m['timestamp']?.toDate(),
        }))
      )
    );
  }

  private async getLastVisible(
    q: Query<DocumentData>
  ): Promise<QueryDocumentSnapshot<DocumentData> | null> {
    const snapshot = await getDocs(q);
    return snapshot.docs[snapshot.docs.length - 1] || null;
  }

  // Returns up to totalRecords messages older than lastMessage, oldest-first (ready to prepend
  // directly in front of the current list).
  async getOlderMessage(lastMessage: any, user1: string, user2: string) {
    const chatId = this.getChatId(user1, user2);
    const messagesRef = collection(this.firestore, `chats/${chatId}/messages`);
    const q = query(
      messagesRef,
      orderBy('timestamp', 'desc'),
      where('timestamp', '<', lastMessage.timestamp),
      limit(this.totalRecords)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((d) => {
        const data = d.data() as any;
        return {
          id: d.id,
          ...data,
          isPlaying: false,
          currentTime: 0,
          duration: 0,
          audioObj: null,
          timestamp: data['timestamp']?.toDate(),
        };
      })
      .reverse();
  }

  async getOlderGroupMessage(lastMessage: any, groupId: string) {
    const messagesRef = collection(this.firestore, `group/${groupId}/messages`);
    const q = query(
      messagesRef,
      orderBy('timestamp', 'desc'),
      where('timestamp', '<', lastMessage.timestamp),
      limit(this.totalRecords)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((d) => {
        const data = d.data() as any;
        const message = {
          id: d.id,
          ...data,
          isPlaying: false,
          currentTime: 0,
          duration: 0,
          audioObj: null,
          isGroup: true,
          timestamp: data['timestamp']?.toDate(),
        };
        this.invalidateAttachmentsIfVisibilityChanged(message);
        return message;
      })
      .reverse();
  }

  async sendMessage(
    message: string,
    user1: string,
    userName: string,
    user2: string,
    clientMessageId: string
  ): Promise<string | null> {
    try {
      message = message.trim();
      if (!message) return null;

      await lastValueFrom(
        this.apiService.post(`${this.roleChatApiBase}/send?isNoLoading=true`, {
          message,
          toUserId: user2,
          clientMessageId,
        })
      );

      this.logRealtimeFeed(false, undefined, message, 'text', false, user2);
      return null;
    } catch (ex: any) {
      return ex?.message || 'Unable to send this message';
    }
  }

  async sendGroupMessage(
    message: string,
    fromUser: string,
    userName: string,
    groupId: string,
    clientMessageId: string
  ): Promise<string | null> {
    try {
      message = message.trim();
      if (!message) return null;

      await lastValueFrom(
        this.apiService.post(`${this.roleChatApiBase}/send?isNoLoading=true`, {
          message,
          groupId,
          clientMessageId,
        })
      );

      this.logRealtimeFeed(true, groupId, message, 'text', false);
      return null;
    } catch (ex: any) {
      return ex?.message || 'Unable to send this message';
    }
  }

  getLastMessage(groupId: string) {
    const messagesRef = collection(this.firestore, `group/${groupId}/messages`);
    const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(1));

    return lastValueFrom(
      collectionData(q, { idField: 'id' }).pipe(
        first(),
        map((messages) => (messages.length > 0 ? (messages[0] as any) : null))
      )
    );
  }

  listenLastMessage(groupId: string) {
    const messagesRef = collection(this.firestore, `group/${groupId}/messages`);

    const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(1));

    return collectionData(q, { idField: 'id' });
  }

  async markGroupAsRead(groupId: string, userId: string): Promise<void> {
    const messagesRef = collection(this.firestore, `group/${groupId}/messages`);
    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    const snapshot = await getDocs(q);
    const batch = writeBatch(this.firestore);

    snapshot.forEach((docSnap) => {
      const data: any = docSnap.data();
      if (!data.readBy || !data.readBy.includes(userId)) {
        batch.update(docSnap.ref, {
          readBy: arrayUnion(userId),
        });
      }
    });

    await batch.commit();
  }

  async markGroupPttAsRead(groupId: string, userId: string): Promise<void> {
    const messagesRef = collection(this.firestore, `group/${groupId}/ptt`);
    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    const snapshot = await getDocs(q);
    const batch = writeBatch(this.firestore);

    snapshot.forEach((docSnap) => {
      const data: any = docSnap.data();
      if (!data.readBy || !data.readBy.includes(userId)) {
        batch.update(docSnap.ref, {
          readBy: arrayUnion(userId),
        });
      }
    });

    await batch.commit();
  }

  async markChatAsRead(chatId: string, userId: string): Promise<void> {
    const messagesRef = collection(this.firestore, `chats/${chatId}/messages`);
    const q = query(messagesRef, orderBy('timestamp', 'desc'));

    const snapshot = await getDocs(q);
    const batch = writeBatch(this.firestore);

    snapshot.forEach((docSnap) => {
      const data: any = docSnap.data();
      if (!data.readBy || !data.readBy.includes(userId)) {
        batch.update(docSnap.ref, {
          readBy: arrayUnion(userId),
        });
      }
    });

    await batch.commit();
  }

  public async createNotificationWithReadBy(
    groupId: string,
    senderId: string,
    message: string
  ) {
    const notificationsRef = collection(this.firestore, 'notifications');
    await addDoc(notificationsRef, {
      type: 'group',
      groupId,
      senderId,
      message,
      createdAt: serverTimestamp(),
      readBy: [senderId],
    });
  }

  saveUserToChatHistory(model: any) {
    return this.apiService.post(`/chat/history/add?isNoLoading=true`, model);
  }

  getConnectedUsers() {
    return this.apiService.get(`/chat/history/users?isNoLoading=true`);
  }

  saveUserToContact(userId: string, linkedUserId: string) {
    return this.apiService.post(`/chat/contact/add/${userId}/${linkedUserId}`);
  }

  getContactListOfUser() {
    return this.apiService.get(`/chat/contact/users`);
  }

  getCurrentUserLink(userId: string) {
    return this.apiService.get(`/chat/contact/${userId}`);
  }

  getCurrentUserLinkWithoutLoading(userId: string) {
    return this.apiService.get(`/chat/contact/${userId}?isNoLoading=true`);
  }

  sendContactNotificationToSupervisor(model: any) {
    return this.apiService.post(
      `/chat/send-contact-notification-to-supervisor`,
      model
    );
  }

  async requestPermission() {
    const perm = await VoiceRecorder.requestAudioRecordingPermission();
    if (!perm.value) {
      alert('Microphone permission denied');
    }
  }

  async startRecording(userId: string) {
    // const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // this.audioChunks = [];

    // this.mediaRecorder = new MediaRecorder(stream);
    // this.mediaRecorder.ondataavailable = e => this.audioChunks.push(e.data);
    // this.mediaRecorder.onstop = () => this.processRecording(userId);
    // this.mediaRecorder.start();
    // console.log('🎙️ Recording started...');
    await this.requestPermission();
    await VoiceRecorder.startRecording();
  }

  async stopRecording() {
    // if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
    //   this.mediaRecorder.stop();
    //   console.log('🛑 Recording stopped');
    // }
    const result = await VoiceRecorder.stopRecording();

    const base64Audio = result.value.recordDataBase64
      ? result.value.recordDataBase64.toString()
      : '';
    const mimeType = result.value.mimeType;
    const byteCharacters = atob(base64Audio);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    this.recordedAudioBlob = new Blob([byteArray], { type: mimeType });

    this.audioUrl = URL.createObjectURL(this.recordedAudioBlob);
  }

  async processRecording(userId: string): Promise<string> {
    const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
    this.audioUrl = URL.createObjectURL(audioBlob);

    return this.audioUrl;
  }

  playRecording() {
    const audio = new Audio(this.audioUrl);
    audio.play();
  }

  async uploadToFirebase(userId: string, userName: string, chatId: string, toUserId?: string) {
    //const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
    const sentAt = Timestamp.now();
    this.uploadAudio(this.recordedAudioBlob, userId).subscribe(async (res) => {
      if (res) {
        const url = res.file;
        const fileName = res.fileName;
        await addDoc(collection(this.firestore, `chats/${chatId}/messages`), {
          url,
          fileName,
          user: userId,
          userName: userName,
          timestamp: sentAt,
          type: 'audio',
          readBy: [userId],
        });

        this.logRealtimeFeed(false, undefined, '', 'audio', false, toUserId);
      }
    });
  }

  async uploadToFirebaseGroup(
    userId: string,
    userName: string,
    groupId: string
  ) {
    //const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
    const sentAt = Timestamp.now();
    this.uploadAudio(this.recordedAudioBlob, userId).subscribe(async (res) => {
      if (res) {
        const url = res.file;
        const fileName = res.fileName;
        await addDoc(collection(this.firestore, `group/${groupId}/messages`), {
          url,
          fileName,
          user: userId,
          userName: userName,
          timestamp: sentAt,
          type: 'audio',
          readBy: [userId],
        });

        this.logRealtimeFeed(true, groupId, '', 'audio', false);
      }
    });
  }

  async uploadToPttGroup(userId: string, userName: string, groupId: string) {
    //const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
    const sentAt = Timestamp.now();
    this.uploadAudio(this.recordedAudioBlob, userId).subscribe(async (res) => {
      if (res) {
        const url = res.file;
        const fileName = res.fileName;
        await addDoc(collection(this.firestore, `group/${groupId}/ptt`), {
          url,
          fileName,
          user: userId,
          userName: userName,
          timestamp: sentAt,
          type: 'audio',
          readBy: [userId],
        });

        this.logRealtimeFeed(true, groupId, '', 'audio', true);
      }
    });
  }

  uploadAudio(audioBlob: Blob, id: string) {
    const filename = `ptt-${uuidv4()}.webm`;
    const formData = new FormData();
    formData.append('file', audioBlob, filename); // 👈 optional filename

    return this.apiService.postFile(`/chat/audio/upload/${id}`, formData);
  }

  // ATTACHMENTS //////////

  private attachmentConfig$?: Observable<AttachmentConfig>;
  // Last config resolved from the API, kept for synchronous reads (see canDeleteMessage).
  private resolvedAttachmentConfig: AttachmentConfig | null = null;

  // Loaded once from the API and shared; the server validates again on upload.
  // Knights have no permission claim, so the API gives them their own upload/view endpoints; admins
  // use the ones protected by the Chat permission.
  // One controller serves messages and attachments. Delete and config are shared by every role; send,
  // upload, visibility and view have a separate "knight" route (admins are gated by permission there).
  private readonly chatApiBase = '/chat/messages';
  private readonly attachmentApiBase = '/chat/messages/attachments';

  private get isKnightCaller(): boolean {
    const roles = (this.authService.currentUserValue?.roles || '')
      .split(',')
      .map((r) => r.trim());
    return roles.includes(Role.Knight) || roles.includes(Role.KnightLeader);
  }

  private get roleChatApiBase(): string {
    return this.isKnightCaller ? `${this.chatApiBase}/knight` : this.chatApiBase;
  }

  private get roleAttachmentApiBase(): string {
    return this.isKnightCaller ? `${this.attachmentApiBase}/knight` : this.attachmentApiBase;
  }

  // private get attachmentApiBase(): string {
  //   const roles = (this.authService.currentUserValue?.roles || '')
  //     .split(',')
  //     .map((r) => r.trim());
  //   const isKnight = roles.includes(Role.Knight) || roles.includes(Role.KnightLeader);
  //   return isKnight ? '/chat/attachments/knight' : '/chat/attachments';
  // }

  // private get chatMessageApiBase(): string {
  //   const roles = (this.authService.currentUserValue?.roles || '')
  //     .split(',')
  //     .map((r) => r.trim());
  //   const isKnight = roles.includes(Role.Knight) || roles.includes(Role.KnightLeader);
  //   return isKnight ? '/chat/messages/knight' : '/chat/messages';
  // }

  getAttachmentConfig(): Observable<AttachmentConfig> {
    this.ensureCacheIsForCurrentUser();
    if (!this.attachmentConfig$) {
      this.attachmentConfig$ = this.apiService
        .get(`${this.attachmentApiBase}/config?isNoLoading=true`)
        .pipe(
          tap((config: AttachmentConfig) => (this.resolvedAttachmentConfig = config)),
          shareReplay(1)
        );
    }
    return this.attachmentConfig$;
  }

  validateAttachment(file: File, config: AttachmentConfig): string | null {
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    if (!extension || !config.allowedExtensions.includes(extension)) {
      return 'This file type is not allowed';
    }
    if (file.size > config.maxFileSizeMb * 1024 * 1024) {
      return `File size must not exceed ${config.maxFileSizeMb} MB`;
    }
    return null;
  }

  // Validates the picked files against the tenant config and builds the local previews shown in the
  // input box. Invalid or duplicated files are skipped and reported in errors.
  async preparePendingAttachments(
    files: File[],
    existing: PendingAttachment[]
  ): Promise<{ added: PendingAttachment[]; errors: string[] }> {
    let config: AttachmentConfig;
    try {
      config = await lastValueFrom(this.getAttachmentConfig());
    } catch {
      config = DEFAULT_ATTACHMENT_CONFIG;
    }

    const added: PendingAttachment[] = [];
    const errors: string[] = [];
    for (const file of files) {
      if (existing.length + added.length >= config.maxFilesPerMessage) {
        errors.push(`You can attach at most ${config.maxFilesPerMessage} files per message`);
        break;
      }

      const isDuplicate = [...existing, ...added].some(
        (p) =>
          p.file.name === file.name &&
          p.file.size === file.size &&
          p.file.lastModified === file.lastModified
      );
      if (isDuplicate) continue;

      const error = this.validateAttachment(file, config);
      if (error) {
        errors.push(`${file.name}: ${error}`);
        continue;
      }

      added.push({
        file,
        previewUrl: file.type.startsWith('image/')
          ? URL.createObjectURL(file)
          : null,
      });
    }
    return { added, errors };
  }

  revokePendingAttachment(pending: PendingAttachment) {
    if (pending.previewUrl) URL.revokeObjectURL(pending.previewUrl);
  }

  // Used only as a fallback until the real attachment config has loaded (see canDeleteMessage), or if it fails to load.
  readonly mockDeleteWindowHours = 24;

  // Replaced by real attachment sending (see sendAttachments) now that the backend enforces
  // per-viewer visibility; kept for reference only.
  // buildSimulatedMessage(
  //   pending: PendingAttachment[],
  //   text: string,
  //   userId: string,
  //   userName: string,
  //   isGroup: boolean,
  //   groupVisibility?: 'AdminOnly' | 'ShareAll'
  // ) {
  //   return {
  //     id: `simulated-${Date.now()}`,
  //     message: (text || '').trim(),
  //     user: userId,
  //     userName,
  //     timestamp: new Date(),
  //     type: 'attachments',
  //     isSimulated: true,
  //     isGroup,
  //     // Only meaningful for group messages; a 1-1 message is always visible to both sides.
  //     groupVisibility: isGroup ? groupVisibility || 'AdminOnly' : undefined,
  //     deleted: false,
  //     previewBlocked: false,
  //     attachments: pending.map(
  //       (p): AttachmentItem => ({
  //         fileName: p.file.name,
  //         fileType: p.file.type || 'application/octet-stream',
  //         fileSize: p.file.size,
  //         previewUrl: p.previewUrl ?? URL.createObjectURL(p.file),
  //       })
  //     ),
  //   };
  // }

  // Whole-message delete/visibility: acts on the entire message (text + every attachment) together,
  // never on just one part of it.
  canDeleteMessage(message: any, currentUserId: string): boolean {
    if (!message || message.isDeleted || message.user !== currentUserId) return false;

    // NULL means the tenant has not set a time limit: the message can always be deleted.
    const windowHours = this.resolvedAttachmentConfig
      ? this.resolvedAttachmentConfig.attachmentDeleteWindowHours
      : this.mockDeleteWindowHours;
    if (windowHours === null) return true;

    const ageMs = Date.now() - new Date(message.timestamp).getTime();
    return ageMs <= windowHours * 60 * 60 * 1000;
  }

  // Visibility only ever applies to messages that carry attachments.
  canToggleMessageVisibility(message: any, currentUserId: string, isAdminViewer: boolean): boolean {
    //console.log('canToggleMessageVisibility', message, currentUserId, isAdminViewer);
    if (!message || message.isDeleted || !message.isGroup) return false;
    //if (!(message.attachments || []).length) return false;

    return message.user === currentUserId || isAdminViewer;
  }

  // Uploads all the files of one message with a single API call (validation, encryption, storage)
  // and then writes ONE message to Firestore. Returns an error message, or null when it was sent.
  async sendAttachments(
    files: File[],
    options: SendAttachmentOptions,
    clientMessageId: string,
    isVoiceMessage = false
  ): Promise<string | null> {
    const sentAt = Timestamp.now();
    try {
      const config = await lastValueFrom(this.getAttachmentConfig());
      if (!files.length) return 'Please select at least one file';

      // A voice message is a built-in feature, not a discretionary attachment type: only the
      // tenant's max file size still applies, not the configured extension whitelist or file count.
      if (isVoiceMessage) {
        const oversized = files.find((f) => f.size > config.maxFileSizeMb * 1024 * 1024);
        if (oversized) return `Recording must not exceed ${config.maxFileSizeMb} MB`;
      } else {
        if (files.length > config.maxFilesPerMessage) {
          return `You can attach at most ${config.maxFilesPerMessage} files per message`;
        }
        for (const file of files) {
          const validationError = this.validateAttachment(file, config);
          if (validationError) return `${file.name}: ${validationError}`;
        }
      }

      const formData = new FormData();
      files.forEach((file) => formData.append('files', file, file.name));
      if (isVoiceMessage) formData.append('isVoiceMessage', 'true');
      if (options.groupId) {
        formData.append('groupId', options.groupId);
        formData.append('groupVisibility', options.groupVisibility || 'AdminOnly');
      } else {
        formData.append('toUserId', options.toUserId as string);
      }

      const res = await lastValueFrom(
        this.apiService.postFile(`${this.roleAttachmentApiBase}/upload?isNoLoading=true`, formData)
      );
      const attachments: AttachmentItem[] = res.attachments;

      const path = options.groupId
        ? `group/${options.groupId}/messages`
        : `chats/${options.chatId}/messages`;

      // clientMessageId is used as the Firestore document id (instead of an auto-generated one) so an
      // optimistic local bubble can be matched against the confirmed doc once the listener delivers it.
      // The message only references the attachments; the files themselves are served by the API.
      // Visibility and delete state apply to the whole message, not per attachment.
      await setDoc(doc(this.firestore, `${path}/${clientMessageId}`), {
        message: (options.text || '').trim(),
        attachments: attachments.map((a) => ({
          attachmentId: a.attachmentId,
          fileName: a.fileName,
          fileType: a.fileType,
          fileSize: a.fileSize,
        })),
        groupVisibility: options.groupId ? options.groupVisibility || 'AdminOnly' : null,
        user: options.fromUserId,
        userName: options.userName,
        timestamp: sentAt,
        type: 'attachments',
        readBy: [options.fromUserId],
        isGroup: !!options.groupId,
        isDeleted: false,
      });

      const onlyImages = attachments.every((a) =>
        (a.fileType || '').startsWith('image/')
      );
      const label = isVoiceMessage ? '[Voice message]' : onlyImages ? '[Image]' : '[File]';
      this.logRealtimeFeed(
        !!options.groupId,
        options.groupId,
        label,
        'attachments',
        false,
        options.toUserId
      );

      return null;
    } catch (ex: any) {
      return ex?.message || 'Unable to send the attachments';
    }
  }

  // Routes a voice recording through the same secure pipeline as images/files (encryption, SQL
  // tracking, permission checks, delete/visibility) instead of the old unencrypted, untracked
  // audio/upload endpoint.
  async sendVoiceMessage(
    blob: Blob,
    options: SendAttachmentOptions,
    clientMessageId: string
  ): Promise<string | null> {
    const extension = (blob.type || 'audio/webm').split('/').pop() || 'webm';
    const file = new File([blob], `voice-${clientMessageId}.${extension}`, {
      type: blob.type || 'audio/webm',
    });
    return this.sendAttachments([file], options, clientMessageId, true);
  }

  // Changes who can view a whole group message's attachments. The Firestore doc is only updated
  // after the API call succeeds, so every viewer's listener picks up the change; there is no local
  // mutation to undo if the API call is rejected.
  async changeGroupVisibility(
    message: any,
    groupVisibility: 'AdminOnly' | 'ShareAll',
    groupId: string
  ): Promise<string | null> {
    try {
      const attachmentIds: string[] = (message.attachments || [])
        .map((a: AttachmentItem) => a.attachmentId)
        .filter(Boolean);
      if (attachmentIds.length > 0){
      await lastValueFrom(
        this.apiService.post(`${this.roleAttachmentApiBase}/visibility`, {
          ids: attachmentIds,
          groupVisibility,
        })
      );
      }

      

      await lastValueFrom(
        this.apiService.post(`${this.roleChatApiBase}/visibility?isNoLoading=true`, {
          messageId: message.id,
          groupId,
          groupVisibility,
        })
      );
      return null;
    } catch (ex: any) {
      return ex?.message || 'Unable to change who can view this message';
    }
  }

  // Soft-deletes the whole message (text + every attachment together).
  async deleteMessage(
    message: any,
    target: { toUserId?: string; groupId?: string }
  ): Promise<string | null> {
    try {
      const attachmentIds: string[] = (message.attachments || [])
        .map((a: AttachmentItem) => a.attachmentId)
        .filter(Boolean);

      if (attachmentIds.length) {
        await lastValueFrom(
          this.apiService.post(`${this.attachmentApiBase}/delete`, { ids: attachmentIds })
        );
      }

      await lastValueFrom(
        this.apiService.post(`${this.chatApiBase}/delete?isNoLoading=true`, {
          messageId: message.id,
          toUserId: target.toUserId,
          groupId: target.groupId,
        })
      );
      return null;
    } catch (ex: any) {
      return ex?.message || 'Unable to delete this message';
    }
  }

  // Attachment files are shown from local object URLs. Requests are collected for a short moment
  // and sent together (see loadAttachmentBatch), so a chat screen with many images does not need one
  // API call per image; every file is also loaded only once per session.
  private attachmentUrlCache = new Map<string, Promise<string>>();
  private resolvedAttachmentUrls = new Map<string, string>();
  private attachmentQueue: {
    id: string;
    resolve: (url: string) => void;
    reject: (error: any) => void;
  }[] = [];
  private attachmentQueueTimer: any = null;

  getResolvedAttachmentUrl(attachmentId: string): string | undefined {
    this.ensureCacheIsForCurrentUser();
    return this.resolvedAttachmentUrls.get(attachmentId);
  }

  // Drops a previously resolved/in-flight result for one attachment, forcing the next request to
  // hit the server again instead of reusing a result that may no longer reflect its current
  // visibility (e.g. an admin switching a group message back to AdminOnly).
  invalidateResolvedAttachmentUrl(attachmentId: string) {
    this.resolvedAttachmentUrls.delete(attachmentId);
    this.attachmentUrlCache.delete(attachmentId);
  }

  private knownAttachmentVisibility = new Map<string, string | null | undefined>();

  // Message-level groupVisibility applies to every attachment on that message, so when it changes,
  // every one of that message's cached attachment URLs must be invalidated together.
  private invalidateAttachmentsIfVisibilityChanged(message: any) {
    if (!message?.id) return;
    const previous = this.knownAttachmentVisibility.get(message.id);
    const current = message.groupVisibility;
    this.knownAttachmentVisibility.set(message.id, current);
    if (previous !== undefined && previous !== current) {
      for (const attachment of (message.attachments || []) as AttachmentItem[]) {
        if (attachment.attachmentId) this.invalidateResolvedAttachmentUrl(attachment.attachmentId);
      }
    }
  }

  resolveAttachmentUrl(attachmentId: string): Promise<string> {
    this.ensureCacheIsForCurrentUser();
    let cached = this.attachmentUrlCache.get(attachmentId);
    if (!cached) {
      cached = new Promise<string>((resolve, reject) => {
        this.attachmentQueue.push({ id: attachmentId, resolve, reject });
        this.scheduleAttachmentBatch();
      });
      this.attachmentUrlCache.set(attachmentId, cached);
      // A failed request must not stay cached, otherwise it could never be retried.
      cached.catch(() => this.attachmentUrlCache.delete(attachmentId));
    }
    return cached;
  }

  // Starts loading several attachments together, e.g. all the images of a message when the image
  // viewer opens, so moving between them is instant.
  prefetchAttachmentUrls(attachmentIds: string[]) {
    attachmentIds.forEach((id) => this.resolveAttachmentUrl(id).catch(() => {}));
  }

  private scheduleAttachmentBatch() {
    if (this.attachmentQueueTimer) return;
    this.attachmentQueueTimer = setTimeout(() => {
      this.attachmentQueueTimer = null;
      const queue = this.attachmentQueue;
      this.attachmentQueue = [];
      for (let i = 0; i < queue.length; i += ATTACHMENT_BATCH_SIZE) {
        this.loadAttachmentBatch(queue.slice(i, i + ATTACHMENT_BATCH_SIZE));
      }
    }, 30);
  }

  private async loadAttachmentBatch(batch: { id: string; resolve: (url: string) => void; reject: (error: any) => void }[]) {
    try {
      const res = await lastValueFrom(
        this.apiService.post(`${this.roleAttachmentApiBase}/batch?isNoLoading=true`, {
          ids: batch.map((b) => b.id),
        })
      );

      const results = new Map<string, any>(
        (res.items || []).map((i: any) => [String(i.attachmentId).toLowerCase(), i])
      );

      for (const item of batch) {
        const result = results.get(item.id.toLowerCase());
        if (result?.contentBase64) {
          const response = await fetch(
            `data:${result.fileType};base64,${result.contentBase64}`
          );
          const url = URL.createObjectURL(await response.blob());
          this.resolvedAttachmentUrls.set(item.id, url);
          item.resolve(url);
        } else {
          item.reject(new AttachmentAccessError(result?.error, this.attachmentErrorMessage(result?.error)));
        }
      }
    } catch (err) {
      batch.forEach((b) => b.reject(err));
    }
  }

  private attachmentErrorMessage(code?: string): string {
    if (code === 'forbidden') return 'You do not have permission to view this attachment';
    if (code === 'not_found') return 'This attachment is no longer available';
    if (code === 'admin_only') return 'Only users with permission may view this message.';
    if (code === 'deleted') return 'This message was deleted';
    return 'Unable to load this attachment';
  }

  // CHAT GROUP //////////

  getAllGroups() {
    return this.apiService.get(`/chat/group/all`);
  }

  getAllGroupsWithouLoading() {
    return this.apiService.get(`/chat/group/all?isNoLoading=true`);
  }

  getGroup(groupId: string) {
    return this.apiService.get(`/chat/group/${groupId}`);
  }

   getGroupWithoutLoading(groupId: string) {
    return this.apiService.get(`/chat/group/${groupId}` + `#isNoLoading=true`);
  }

  getGroup2(rosterId: string, shiftId: string) {
    return this.apiService.get(`/chat/group/${rosterId}/${shiftId}`);
  }

  getAllMembersGroup(groupId: string) {
    return this.apiService.get(`/chat/group/members/${groupId}`);
  }

  listenGroupMessages(groupId: string) {
    const messagesRef = collection(this.firestore, `group/${groupId}/messages`);

    const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(11));

    return collectionData(q, { idField: 'id' });
  }

  listenChatMessages(userId: string) {
    const messagesRef = collection(this.firestore, `chats/${userId}/messages`);
    const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(11));
    return collectionData(q, { idField: 'id' });
  }

  listenPttGroupMessage(groupId: string) {
    const messagesRef = collection(this.firestore, `group/${groupId}/ptt`);
    const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(11));
    return collectionData(q, { idField: 'id' });
  }

  private chats = new BehaviorSubject<{ id: string; name: string }[]>([]);
  chats$ = this.chats.asObservable();

  openChat(user: { id: string; name: string }) {
    const current = this.chats.value;
    if (!current.find(c => c.id === user.id)) {
      this.chats.next([...current, user]);
    }
  }

  closeChat(userId: string) {
    this.chats.next(this.chats.value.filter(c => c.id !== userId));
  }
}
